import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import './StaffHealthProgramsPage.css';

const StaffHealthProgramsPage = () => {
  const [programs, setPrograms] = useState([]);
  const [filteredPrograms, setFilteredPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterCategory, setFilterCategory] = useState('All');
  const [programCategories, setProgramCategories] = useState([]);
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Fetch program categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/api/program-categories?activeOnly=true');
        setProgramCategories(response.data.categories || []);
      } catch (err) {
        console.error('Error fetching program categories:', err);
      }
    };
    fetchCategories();
  }, []);

  // Redirect if user doesn't have program access
  useEffect(() => {
    if (currentUser && !(currentUser.health || currentUser.education)) {
      navigate('/staff-dashboard');
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam && categoryParam !== filterCategory) {
      setFilterCategory(categoryParam);
    }
  }, [searchParams, filterCategory]);

  useEffect(() => {
    fetchPrograms();
  }, []);

  const filterPrograms = useCallback(() => {
    let filtered = programs;
    
    if (filterCategory !== 'All') {
      filtered = filtered.filter(p => p.category === filterCategory);
    }
    
    if (filterType !== 'All') {
      filtered = filtered.filter(p => p.programType === filterType);
    }
    
    if (filterStatus !== 'All') {
      filtered = filtered.filter(p => p.status === filterStatus);
    }
    
    setFilteredPrograms(filtered);
  }, [programs, filterCategory, filterType, filterStatus]);

  useEffect(() => {
    filterPrograms();
  }, [filterPrograms]);

  const fetchPrograms = async () => {
    try {
      const response = await api.get('/api/health-programs');
      setPrograms(response.data.programs || []);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching programs');
      setLoading(false);
    }
  };



  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this health program?')) {
      try {
        await api.delete(`/api/health-programs/${id}`);
        setPrograms(programs.filter((p) => p._id !== id));
        setSuccessMessage('Health program deleted successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (err) {
        alert(err.response?.data?.message || 'Error deleting health program');
      }
    }
  };

  const handleTogglePublish = async (id) => {
    try {
      await api.patch(`/api/health-programs/${id}/publish`);
      fetchPrograms();
      setSuccessMessage('Program publish status updated');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      alert(err.response?.data?.message || 'Error updating publish status');
    }
  };

  const handleToggleFeature = async (id) => {
    try {
      await api.patch(`/api/health-programs/${id}/feature`);
      fetchPrograms();
      setSuccessMessage('Program featured status updated');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      alert(err.response?.data?.message || 'Error updating featured status');
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const response = await api.get(`/api/health-programs/${id}`);
      const programData = response.data.program;
      
      const newProgramData = {
        ...programData,
        title: `${programData.title} (Copy)`,
        _id: undefined,
        createdBy: undefined,
        updatedBy: undefined,
        isPublished: false,
        status: 'draft'
      };
      
      await api.post('/api/health-programs', newProgramData);
      setSuccessMessage(`Successfully duplicated "${programData.title}"`);
      fetchPrograms();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      alert(err.response?.data?.message || 'Error duplicating program');
    }
  };

  // Get dynamic program types based on current filter
  const getProgramTypes = () => {
    const currentPrograms = filterCategory !== 'All' 
      ? programs.filter(p => p.category === filterCategory)
      : programs;
    
    const allTypes = currentPrograms.flatMap(p =>
      Array.isArray(p.programType) ? p.programType : (p.programType ? [p.programType] : [])
    );
    const uniqueTypes = [...new Set(allTypes)].sort();
    return ['All', ...uniqueTypes];
  };

  const programTypes = getProgramTypes();

  if (loading) return <div className="loading">Loading programs...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="staff-health-programs-container">
      <div className="page-header">
        <h1>📋 Programs Management</h1>
        <div className="action-buttons">
          <Link to="/create-health-program" className="btn btn-primary">
            + Create New Program
          </Link>
          <Link 
            to={currentUser?.education && !currentUser?.health ? '/education-applications' : '/health-applications'} 
            className="btn btn-info"
          >
            📋 View Applications
          </Link>
          <Link to="/file-manager" className="btn btn-info">
            📁 File Manager
          </Link>
          <Link to="/admin/program-categories" className="btn btn-secondary">
            ⚙️ Manage Categories
          </Link>
          <Link
            to={filterCategory !== 'All'
              ? `/health-programs?category=${encodeURIComponent(filterCategory)}`
              : '/health-programs'
            }
            className="btn btn-secondary"
          >
            View Public Page
          </Link>
          <Link to="/staff-dashboard" className="btn btn-secondary">
            Back to Dashboard
          </Link>
        </div>
      </div>

      {successMessage && <div className="success-message">{successMessage}</div>}

      <div className="filters-section">
        <div className="filter-group">
          <label>Category:</label>
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
            <option value="All">All Categories</option>
            {programCategories.map(cat => (
              <option key={cat.key} value={cat.key}>{cat.icon} {cat.label}</option>
            ))}
          </select>
        </div>
        
        <div className="filter-group">
          <label>Program Type:</label>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            {programTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        
        <div className="filter-group">
          <label>Status:</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="All">All</option>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="archived">Archived</option>
          </select>
        </div>
        
        <div className="filter-stats">
          Showing {filteredPrograms.length} of {programs.length} programs
        </div>
      </div>

      {filteredPrograms.length === 0 ? (
        <div className="no-programs">
          <p>No programs found matching your filters.</p>
        </div>
      ) : (
        <div className="programs-grid">
          {filteredPrograms.map((program) => (
            <div key={program._id} className="program-card">
              <div className="program-header">
                <div>
                  <h3>{program.title}</h3>
                  <div className="program-badges">
                    {program.category && (
                      <span className={`category-badge category-${program.category}`}>
                        {programCategories.find(c => c.key === program.category)?.icon || '📋'} {program.category}
                      </span>
                    )}
                    {Array.isArray(program.programType)
                      ? program.programType.map((type, i) => (
                          <span key={i} className="program-type-badge">{type}</span>
                        ))
                      : program.programType && (
                          <span className="program-type-badge">{program.programType}</span>
                        )
                    }
                  </div>
                </div>
                <div className="program-status">
                  {program.isFeatured && <span className="badge featured">⭐ Featured</span>}
                  {program.isPublished ? (
                    <span className="badge published">✓ Published</span>
                  ) : (
                    <span className="badge draft">Draft</span>
                  )}
                  <span className={`badge status-${program.status}`}>
                    {program.status}
                  </span>
                </div>
              </div>

              <p className="program-description">{(() => { const t = document.createElement('div'); t.innerHTML = program.description; const plain = (t.textContent || t.innerText || '').replace(/\s+/g, ' ').trim(); return plain.length > 150 ? `${plain.substring(0, 150)}...` : plain; })()}</p>

              <div className="program-meta">
                <div className="meta-item">
                  <strong>Target:</strong> {Array.isArray(program.targetPopulation) ? program.targetPopulation.join(', ') : program.targetPopulation}
                </div>
                {program.applicationDeadline && (
                  <div className="meta-item">
                    <strong>Deadline:</strong> {new Date(program.applicationDeadline).toLocaleDateString()}
                  </div>
                )}
                {program.maxParticipants && (
                  <div className="meta-item">
                    <strong>Capacity:</strong> {program.currentParticipants}/{program.maxParticipants}
                  </div>
                )}
                <div className="meta-item">
                  <strong>Views:</strong> {program.views}
                </div>
              </div>

              <div className="program-actions">
                <Link to={`/edit-health-program/${program._id}`} className="btn-edit">
                  ✏️ Edit
                </Link>
                <button 
                  onClick={() => handleTogglePublish(program._id)}
                  className="btn-toggle"
                >
                  {program.isPublished ? '👁️ Unpublish' : '📢 Publish'}
                </button>
                <button 
                  onClick={() => handleToggleFeature(program._id)}
                  className="btn-toggle"
                >
                  {program.isFeatured ? '⭐ Unfeature' : '⭐ Feature'}
                </button>
                <button 
                  onClick={() => handleDuplicate(program._id)}
                  className="btn-duplicate"
                >
                  📋 Duplicate
                </button>
                <button 
                  onClick={() => handleDelete(program._id)} 
                  className="btn-delete"
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StaffHealthProgramsPage;
